import { useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';

import type { AdminCompanyAdminStatus, AdminUserRow } from '../../../services/api';
import { getAdminUserAuth0SyncState } from '../../../lib/admin-user-auth0-syncability';
import { formatAdminUserLastLogin } from '../../../lib/admin-user-last-login';
import { AdminDashboardPaginatedSection } from '../AdminDashboardPrimitives';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    getClickableRowProps,
} from '../../ui/table';
import { StatusIcon } from '../../ui/status-icon';
import { BreakableEmail } from '../../ui/breakable-email';
import { inlineLinkClassName } from '../../ui/link-styles';
import { cn } from '../../../lib/utils';

const COMPANY_ADMIN_LOADING_REASON = 'Company admin statuses are still loading.';
const COMPANY_ADMIN_ERROR_REASON = 'Company admin statuses could not be loaded.';
const COMPANY_ADMIN_MISSING_REASON = 'Company admin status is unavailable for this user.';
const COMPANY_ADMIN_INACTIVE_REASON = 'User is inactive.';
const COMPANY_ADMIN_UNSYNCED_REASON =
    'User must be synced to Auth0 before company admin access can be changed.';
const COMPANY_ADMIN_NO_ACTIVE_MEMBERSHIP_REASON =
    'User must have an active membership on an active team in this company.';
const COMPANY_ADMIN_PENDING_REASON = 'Updating company admin access.';

function getCompanyUserAccessibleName(user: Pick<AdminUserRow, 'name' | 'email'>): string {
    return user.name || user.email;
}

function getSyncButtonAccessibleName(
    user: Pick<AdminUserRow, 'name' | 'email'>,
    isSyncing: boolean,
): string {
    return (isSyncing ? 'Syncing…' : 'Sync') + ' ' + getCompanyUserAccessibleName(user);
}

function formatUserCompanyLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.companyNames.join(', ') || '—';
    }
    const labels = new Map<number, string>();
    for (const membership of user.memberships) {
        const name = membership.companyName.trim() || 'Unnamed company';
        labels.set(membership.companyId, `${name} (#${membership.companyId})`);
    }
    return [...labels.values()].join(', ') || '—';
}

function formatUserTeamLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.teamNames.join(', ') || '—';
    }
    return (
        user.memberships
            .map((membership) => {
                const name = membership.teamName.trim() || 'Unnamed team';
                return `${name} (#${membership.teamId})`;
            })
            .join(', ') || '—'
    );
}

function getCompanyAdminDisabledReason({
    user,
    status,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    isPendingUser,
}: {
    user: AdminUserRow;
    status: AdminCompanyAdminStatus | undefined;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    isPendingUser: boolean;
}): string | null {
    if (isPendingUser) {
        return COMPANY_ADMIN_PENDING_REASON;
    }
    if (isCompanyAdminStatusPending) {
        return COMPANY_ADMIN_LOADING_REASON;
    }
    if (isCompanyAdminStatusError) {
        return COMPANY_ADMIN_ERROR_REASON;
    }
    if (!status) {
        return COMPANY_ADMIN_MISSING_REASON;
    }
    if (!user.isActive) {
        return COMPANY_ADMIN_INACTIVE_REASON;
    }
    if (!user.isSyncedToAuth0) {
        return COMPANY_ADMIN_UNSYNCED_REASON;
    }
    if (!activeCompanyMemberUserIds.has(user.id)) {
        return COMPANY_ADMIN_NO_ACTIVE_MEMBERSHIP_REASON;
    }
    return null;
}

function CompanyUserRolesCell({
    user,
    status,
    organizationId,
}: {
    user: AdminUserRow;
    status: AdminCompanyAdminStatus | undefined;
    organizationId: string | null;
}) {
    const subjectId = status?.subjectId ?? user.subjectId;
    return (
        <div className="flex min-w-0 flex-col items-start gap-1">
            {status?.roles ? (
                <span className="break-words text-xs text-neutral-700">
                    {status.roles.length > 0 ? [...status.roles].sort().join(', ') : 'No roles'}
                </span>
            ) : null}
            {organizationId && subjectId ? (
                <Link
                    to={`/admin/${encodeURIComponent(organizationId)}/user/${encodeURIComponent(subjectId)}`}
                    className={cn('inline-flex min-h-6 items-center text-xs', inlineLinkClassName)}
                    aria-label={`Manage roles for ${user.name}`}
                    onClick={(event) => event.stopPropagation()}
                >
                    Manage roles
                </Link>
            ) : null}
        </div>
    );
}

function CompanyAdminStatusText({
    status,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
}: {
    status: AdminCompanyAdminStatus | undefined;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
}) {
    if (isCompanyAdminStatusPending) {
        return <span className="text-neutral-500">Loading admin status…</span>;
    }
    if (isCompanyAdminStatusError || !status) {
        return <span className="text-neutral-500">Admin status unavailable</span>;
    }
    return <span>{status.isAdmin ? 'Admin' : 'Not admin'}</span>;
}

export function CompanyUsersPaginatedSection({
    rows,
    page,
    pageSize,
    total,
    totalPages,
    searchQuery,
    onPageChange,
    onRowClick,
    onSync,
    syncingUserId,
    companyAdminStatusByUserId,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    companyAdminPendingUserId,
    onSetCompanyUserAdmin,
    organizationId,
}: {
    rows: AdminUserRow[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    searchQuery?: string;
    onPageChange: (page: number) => void;
    onRowClick: (user: AdminUserRow) => void;
    onSync: (userId: number) => void;
    syncingUserId: number | null;
    companyAdminStatusByUserId: Map<number, AdminCompanyAdminStatus>;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    companyAdminPendingUserId: number | null;
    onSetCompanyUserAdmin: (userId: number, isAdmin: boolean) => void;
    /** Auth0 organization of the company; null until the company is synced to Auth0. */
    organizationId: string | null;
}) {
    const [adminRemovalTarget, setAdminRemovalTarget] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [syncConfirmUser, setSyncConfirmUser] = useState<AdminUserRow | null>(null);
    const syncConfirmLabel = syncConfirmUser ? getCompanyUserAccessibleName(syncConfirmUser) : '';

    return (
        <>
            <AdminDashboardPaginatedSection
                rows={rows}
                page={page}
                pageSize={pageSize}
                total={total}
                totalPages={totalPages}
                onPageChange={onPageChange}
                render={(pageRows) => (
                    <Card className="overflow-clip">
                        <Table
                            stickyHeader
                            aria-label="Users"
                            className="min-w-[1120px] table-fixed"
                        >
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[10%]">Name</TableHead>
                                    <TableHead className="w-[15%]">Email</TableHead>
                                    <TableHead className="w-[10%]">Companies</TableHead>
                                    <TableHead className="w-[13%]">Teams</TableHead>
                                    <TableHead className="w-[4%]">SSO</TableHead>
                                    <TableHead className="w-[5%]">Status</TableHead>
                                    <TableHead className="w-[9%]">Last login</TableHead>
                                    <TableHead className="w-[11%]">Auth0</TableHead>
                                    <TableHead className="w-[12%]">Roles</TableHead>
                                    <TableHead className="w-[11%]">Company admin</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pageRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={10}
                                            className="text-center text-sm text-neutral-600"
                                        >
                                            {searchQuery?.trim() ? (
                                                <>No users match “{searchQuery.trim()}”.</>
                                            ) : (
                                                'No users on teams for this company yet.'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pageRows.map((user) => {
                                        const syncState = getAdminUserAuth0SyncState(user);
                                        const userAccessibleName =
                                            getCompanyUserAccessibleName(user);
                                        const isSyncing = syncingUserId === user.id;
                                        const companyAdminStatus = companyAdminStatusByUserId.get(
                                            user.id,
                                        );
                                        const companyAdminPending =
                                            companyAdminPendingUserId === user.id;
                                        const companyAdminDisabledReason =
                                            getCompanyAdminDisabledReason({
                                                user,
                                                status: companyAdminStatus,
                                                isCompanyAdminStatusPending,
                                                isCompanyAdminStatusError,
                                                activeCompanyMemberUserIds,
                                                isPendingUser: companyAdminPending,
                                            });
                                        const companyAdminDisabled =
                                            companyAdminDisabledReason !== null;
                                        const nextCompanyAdminState = !companyAdminStatus?.isAdmin;
                                        const companyAdminActionLabel = companyAdminPending
                                            ? 'Updating company admin'
                                            : companyAdminStatus?.isAdmin
                                              ? 'Remove company admin'
                                              : 'Grant company admin';

                                        return (
                                            <TableRow
                                                key={user.id}
                                                className="cursor-pointer [&>td]:align-top"
                                                {...getClickableRowProps(() => onRowClick(user))}
                                            >
                                                <TableCell className="break-words hyphens-auto font-medium text-neutral-950">
                                                    {user.name}
                                                </TableCell>
                                                <TableCell className="[overflow-wrap:anywhere]">
                                                    <BreakableEmail email={user.email} />
                                                </TableCell>
                                                <TableCell>
                                                    {formatUserCompanyLabels(user)}
                                                </TableCell>
                                                <TableCell>{formatUserTeamLabels(user)}</TableCell>
                                                <TableCell>
                                                    <StatusIcon
                                                        value={user.isSso}
                                                        falseTone="neutral"
                                                        label={
                                                            user.isSso
                                                                ? 'SSO enabled'
                                                                : 'SSO disabled'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <StatusIcon value={user.isActive} />
                                                </TableCell>
                                                <TableCell className="text-sm text-neutral-700">
                                                    {formatAdminUserLastLogin(user)}
                                                </TableCell>
                                                <TableCell>
                                                    {user.isSyncedToAuth0 ? (
                                                        <StatusIcon value />
                                                    ) : (
                                                        <span className="inline-flex items-center gap-2">
                                                            <StatusIcon value={false} />
                                                            <Button
                                                                type="button"
                                                                variant="destructiveOutline"
                                                                size="sm"
                                                                loading={isSyncing}
                                                                disabled={
                                                                    isSyncing || !syncState.canSync
                                                                }
                                                                title={
                                                                    !syncState.canSync
                                                                        ? (syncState.disabledReason ??
                                                                          undefined)
                                                                        : undefined
                                                                }
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setSyncConfirmUser(user);
                                                                }}
                                                                aria-label={getSyncButtonAccessibleName(
                                                                    user,
                                                                    isSyncing,
                                                                )}
                                                            >
                                                                {!isSyncing && (
                                                                    <RefreshCw aria-hidden />
                                                                )}
                                                                {isSyncing ? 'Syncing…' : 'Sync'}
                                                            </Button>
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <CompanyUserRolesCell
                                                        user={user}
                                                        status={companyAdminStatus}
                                                        organizationId={organizationId}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div
                                                        className="flex flex-col items-start gap-1.5"
                                                        onClick={(event) => event.stopPropagation()}
                                                        onKeyDown={(event) =>
                                                            event.stopPropagation()
                                                        }
                                                    >
                                                        <CompanyAdminStatusText
                                                            status={companyAdminStatus}
                                                            isCompanyAdminStatusPending={
                                                                isCompanyAdminStatusPending
                                                            }
                                                            isCompanyAdminStatusError={
                                                                isCompanyAdminStatusError
                                                            }
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant={
                                                                companyAdminStatus?.isAdmin
                                                                    ? 'destructiveOutline'
                                                                    : 'outline'
                                                            }
                                                            size="sm"
                                                            loading={companyAdminPending}
                                                            disabled={companyAdminDisabled}
                                                            title={
                                                                companyAdminDisabledReason ??
                                                                undefined
                                                            }
                                                            aria-label={
                                                                companyAdminActionLabel +
                                                                ' for ' +
                                                                userAccessibleName
                                                            }
                                                            onClick={() => {
                                                                if (companyAdminDisabled) {
                                                                    return;
                                                                }
                                                                if (!nextCompanyAdminState) {
                                                                    setAdminRemovalTarget({
                                                                        id: user.id,
                                                                        name: user.name,
                                                                    });
                                                                    return;
                                                                }
                                                                onSetCompanyUserAdmin(
                                                                    user.id,
                                                                    nextCompanyAdminState,
                                                                );
                                                            }}
                                                        >
                                                            {companyAdminPending
                                                                ? 'Updating…'
                                                                : companyAdminStatus?.isAdmin
                                                                  ? 'Remove'
                                                                  : 'Grant'}
                                                        </Button>
                                                        {companyAdminDisabledReason ? (
                                                            <span className="text-xs text-neutral-500">
                                                                {companyAdminDisabledReason}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                )}
            />
            <ConfirmDialog
                open={syncConfirmUser !== null}
                destructive
                title={`Sync ${syncConfirmLabel} to Auth0?`}
                description={`${syncConfirmLabel} will be synced to Auth0.`}
                confirmLabel="Sync user"
                pendingLabel="Syncing…"
                onCancel={() => setSyncConfirmUser(null)}
                onConfirm={() => {
                    const user = syncConfirmUser;
                    if (!user) return;
                    setSyncConfirmUser(null);
                    onSync(user.id);
                }}
            />
            <ConfirmDialog
                open={adminRemovalTarget !== null}
                title={`Remove company admin access for ${adminRemovalTarget?.name ?? ''}?`}
                description="Every active team role for this user in this company will reset to Pro User."
                confirmLabel="Remove admin access"
                destructive
                onCancel={() => setAdminRemovalTarget(null)}
                onConfirm={() => {
                    const target = adminRemovalTarget;
                    setAdminRemovalTarget(null);
                    if (target) {
                        onSetCompanyUserAdmin(target.id, false);
                    }
                }}
            />
        </>
    );
}
